import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown } from 'react-icons/fi';

const TOOLBAR_BORDER = '#dee2e6';
/** Closed trigger border — keep light for toolbar match */

/** Open panel: reference-style list (white menu, dark edge, blue row + white text) */
const MENU_BORDER = '#334155';
const MENU_SHADOW = '0 4px 16px rgba(15, 23, 42, 0.14), 0 1px 3px rgba(15, 23, 42, 0.08)';
/** Compact rows — minimal vertical padding (concise list like date-range dropdowns). */
const ROW_PADDING = '6px 10px';
const ROW_FONT_SIZE = 12;
const HIGHLIGHT_BG = '#2563eb';
const ROW_TEXT = '#0f172a';
const MENU_VIEWPORT_GUTTER = 8;
const MENU_TRIGGER_GAP = 4;
const MENU_MIN_LIST_HEIGHT = 60;
const MENU_ROW_ESTIMATE = 30;

const triggerShellStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  paddingLeft: 8,
  paddingRight: 6,
  minHeight: 34,
  height: 34,
  width: '100%',
  border: `1px solid ${TOOLBAR_BORDER}`,
  borderRadius: 6,
  background: '#fff',
  boxSizing: 'border-box',
  minWidth: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
};

const labelStyle = {
  flex: 1,
  minWidth: 0,
  fontSize: 12,
  fontWeight: 500,
  color: '#344054',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function rowIsSelected(value, rowValue) {
  const empty = value === '' || value == null;
  const rowEmpty = rowValue === '' || rowValue == null;
  if (empty && rowEmpty) return true;
  if (empty || rowEmpty) return false;
  return String(value) === String(rowValue);
}

/**
 * Custom dropdown list (native `<select>` cannot style the open list).
 * Panel: flat white list, compact row padding, full-width blue highlight + white text on hover/selection.
 * Uses a fixed-position portal so menus are not clipped by overflow (e.g. table wrappers).
 */
export default function StyledSelectDropdown({
  value,
  onChange,
  placeholder,
  options = [],
  disabled = false,
  leftIcon = null,
  isLoading = false,
  loadingText = 'Loading...',
  emptyOptionLabel,
  maxListHeight = 320,
  zIndex = 12000,
  ariaLabel,
  /** When true, list only contains `options` (no blank first row). */
  omitEmptyOption = false,
  /** Override trigger border, e.g. `#e5e7eb`. */
  triggerBorderColor,
  /** Pixel height for trigger (default 34). */
  triggerHeight,
}) {
  const [open, setOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const emptyLabel = emptyOptionLabel ?? placeholder;

  const rows = omitEmptyOption
    ? options.map((o, index) => ({
        key: `v-${index}-${String(o.value)}`,
        value: o.value,
        label: o.label,
      }))
    : [
        { key: '__empty', value: '', label: emptyLabel },
        ...options.map((o) => ({ key: String(o.value), value: o.value, label: o.label })),
      ];

  const updateMenuRect = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = Math.max(0, viewportHeight - r.bottom - MENU_VIEWPORT_GUTTER - MENU_TRIGGER_GAP);
    const spaceAbove = Math.max(0, r.top - MENU_VIEWPORT_GUTTER - MENU_TRIGGER_GAP);
    const estimatedMenuHeight = Math.min(maxListHeight, rows.length * MENU_ROW_ESTIMATE + 2);
    const shouldOpenUp =
      spaceBelow < Math.min(estimatedMenuHeight, 180) && spaceAbove > spaceBelow;
    const availableHeight = shouldOpenUp ? spaceAbove : spaceBelow;
    const listMaxHeight = Math.min(
      maxListHeight,
      Math.max(MENU_MIN_LIST_HEIGHT, availableHeight || maxListHeight)
    );
    const menuHeightForPosition = Math.min(listMaxHeight, estimatedMenuHeight);
    const top = shouldOpenUp
      ? Math.max(MENU_VIEWPORT_GUTTER, r.top - MENU_TRIGGER_GAP - menuHeightForPosition)
      : Math.max(MENU_VIEWPORT_GUTTER, r.bottom + MENU_TRIGGER_GAP);

    setMenuRect({
      left: r.left,
      top,
      width: r.width,
      listMaxHeight,
    });
  }, [maxListHeight, rows.length]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      setHoverKey(null);
      return;
    }
    updateMenuRect();
    const onScrollOrResize = () => updateMenuRect();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updateMenuRect]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value));
  const displayText = isLoading
    ? loadingText
    : selected
      ? selected.label
      : emptyLabel;

  const handlePick = (next) => {
    onChange(String(next));
    setOpen(false);
  };

  const triggerDisabled = disabled || isLoading;

  const triggerMergedStyle = {
    ...triggerShellStyle,
    ...(triggerBorderColor ? { border: `1px solid ${triggerBorderColor}` } : {}),
    ...(typeof triggerHeight === 'number'
      ? { height: triggerHeight, minHeight: triggerHeight }
      : {}),
  };

  const menuContent =
    open && menuRect ? (
      <div
        ref={menuRef}
        role="listbox"
        aria-label={placeholder}
        style={{
          position: 'fixed',
          left: menuRect.left,
          top: menuRect.top,
          width: menuRect.width,
          zIndex,
          background: '#fff',
          border: `1px solid ${MENU_BORDER}`,
          borderRadius: 4,
          boxShadow: MENU_SHADOW,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            maxHeight: menuRect.listMaxHeight ?? maxListHeight,
            overflowY: 'auto',
          }}
        >
          {rows.map((row, index) => {
            const isSel = rowIsSelected(value, row.value);
            const showHighlight =
              hoverKey === row.key || (hoverKey === null && isSel);
            return (
              <button
                key={row.key === '__empty' ? '__empty' : `${row.key}-${index}`}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => handlePick(row.value)}
                onMouseEnter={() => setHoverKey(row.key)}
                onMouseLeave={() => setHoverKey(null)}
                style={{
                  display: 'block',
                  width: '100%',
                  margin: 0,
                  padding: ROW_PADDING,
                  border: 'none',
                  borderBottom: 'none',
                  background: showHighlight ? HIGHLIGHT_BG : '#ffffff',
                  color: showHighlight ? '#ffffff' : ROW_TEXT,
                  fontSize: ROW_FONT_SIZE,
                  lineHeight: 1.28,
                  fontWeight: isSel ? 500 : 400,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                {row.label}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

  return (
    <div style={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        disabled={triggerDisabled}
        onClick={() => !triggerDisabled && setOpen((o) => !o)}
        aria-label={ariaLabel ?? placeholder}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...triggerMergedStyle,
          opacity: triggerDisabled ? 0.65 : 1,
          cursor: triggerDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {leftIcon}
        <span style={labelStyle}>{displayText}</span>
        <FiChevronDown
          size={14}
          aria-hidden
          style={{
            flexShrink: 0,
            color: '#98a2b3',
            transform: open ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.15s ease',
          }}
        />
      </button>
      {typeof document !== 'undefined' && menuContent ? createPortal(menuContent, document.body) : null}
    </div>
  );
}
